import _ from "lodash";
import React, { useState, useCallback, useEffect } from "react";
import classes from "./Sidebar.scss";
import classNames from "classnames";
import { colors } from "Constants";
import { useI18n } from "Hooks";
import {
  getHTMLLabel,
  shouldNodeRender,
  AUTOMATIC_SCROLL,
  MANUAL_SCROLL,
} from "../GenericTableEditorUtils";
import { ListDashedOutlined, CloseOutlined } from "@toddle-edu/ds-icons";

const Sidebar = props => {
  const {
    nodes,
    rootNodeId,
    searchText,
    showNoResultsFound,
    nodeRefs,
    filteredNodeIds,
    scrollBehaviourRef,
    selectedrNodes,
    updateSelectedNodes,
  } = props;

  useEffect(() => {
    return () => {
      updateSelectedNodes([]);
    };
  }, []);

  const { t } = useI18n(["common", "academicSetup", "a11y"]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const containerClasses = classNames({
    [classes.containerOpen]: isSidebarOpen,
    [classes.containerClose]: !isSidebarOpen,
  });

  const innerContainerClasses = classNames({
    [classes.innerContainerHidden]: !isSidebarOpen,
    [classes.innerContainerShow]: isSidebarOpen,
  });

  const toggleSidebar = () => setIsSidebarOpen(isSidebarOpen => !isSidebarOpen);

  const onSelectNode = useCallback(({ id }) => {
    updateSelectedNodes([id]);

    if (nodeRefs.current[id]) {
      scrollBehaviourRef.current = AUTOMATIC_SCROLL;

      nodeRefs.current[id].scrollIntoView({
        block: "center",
      });

      setTimeout(() => {
        scrollBehaviourRef.current = MANUAL_SCROLL;
      }, 300);
    }
  }, []);

  const headerClasses = classNames(classes.header, "text-label-3");

  return (
    <div className={containerClasses}>
      {isSidebarOpen ? (
        <button
          className={classes.closeButton}
          onClick={toggleSidebar}
          aria-label={t("a11y:close_sidebar")}
        >
          <CloseOutlined size={"xx-small"} />
        </button>
      ) : (
        <button
          className={classes.openButton}
          onClick={toggleSidebar}
          aria-label={t("a11y:open_sidebar")}
        >
          <ListDashedOutlined variant={"on"} size={"xx-small"} />
        </button>
      )}

      <div className={innerContainerClasses}>
        <div className={headerClasses}>{t("common:outline")}</div>

        <div className={classes.itemsContainer}>
          {showNoResultsFound ? (
            <NoDataView />
          ) : (
            <RenderSideBarRows
              nodes={nodes}
              rootNodeId={rootNodeId}
              selectedNodes={selectedrNodes}
              onSelectNode={onSelectNode}
              searchText={searchText}
              filteredNodeIds={filteredNodeIds}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const NoDataView = () => {
  const { t } = useI18n("academicSetup");

  return (
    <div className={classes.noDataContainer}>
      {t("academicSetup:empty_list_items_message")}
    </div>
  );
};

const RenderSideBarRows = ({
  nodes,
  rootNodeId,
  filteredNodeIds,
  searchText,
  selectedNodes,
  onSelectNode,
}) => {
  /**
   * Only consider those children whose corresponding nodes are present in the nodes object
   */
  const rootNodeChildren = _.filter(
    _.get(nodes[rootNodeId], "children", []),
    childId => _.has(nodes, childId)
  );

  const { t } = useI18n(["common"]);

  const placeholderLabel = t("common:enter_with_label", {
    label: t("common:list_item_one"),
  });

  return (
    <>
      {_.map(rootNodeChildren, childId => {
        const { label } = nodes[childId];

        const isLabelFilled = !!label;

        const isNodeSelected = _.includes(selectedNodes, childId);

        const itemContainer = classNames(classes.itemContainer, {
          [classes.itemContainerSelected]: isNodeSelected,
        });

        const labelContainer = classNames(classes.label, {
          [classes.labelSelected]: isNodeSelected,
          [classes.noLabel]: !isLabelFilled,
          ["heading-6"]: isNodeSelected,
          ["text-subtitle-2"]: !isNodeSelected,
        });

        const handleSetSelectedNodeId = () => {
          onSelectNode({
            id: childId,
          });
        };

        const shouldRowRender = shouldNodeRender({
          filteredNodeIds,
          rootNodeId: childId,
        });

        if (!shouldRowRender) return null;

        const newLabel = getHTMLLabel({
          searchText,
          label,
          style: { color: colors.blue400 },
        });
        return (
          <React.Fragment key={childId}>
            <button
              className={itemContainer}
              onClick={handleSetSelectedNodeId}
              aria-label={label || placeholderLabel}
            >
              <div
                className={labelContainer}
                dangerouslySetInnerHTML={{
                  __html: isLabelFilled ? newLabel : placeholderLabel,
                }}
              />
            </button>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default Sidebar;
